// @ts-check
// =============================================================================
// js/net/bot-runner.js — load + drive the client-side bot AI.
//
// WHAT THIS IS (plan/challenge-ladder.md)
//   Single-player duels use the SAME wire protocol as multiplayer, but the
//   server's BotTurn is a stub — the CLIENT runs the bot's brain. The bot
//   scripts are self-hosted at /bots/ (BotTools + BoringHuman, HornyBunny,
//   FastGoblin, SmartFox, SmugTiger). They were written against the old
//   client's globals, so this runner:
//
//     1. installs the globals they expect BEFORE loading them:
//          GetSources / GetTargets  -> the shared rules (same bundle we use)
//          SocketAction(action)     -> our sendAction (the human's socket
//                                      performs the bot's moves in Bot games;
//                                      the server flips the board per actor)
//          window.biterbots         -> the registry the scripts attach to
//     2. LAZY-LOADS the scripts as classic <script> tags the first time a
//        single-player game is entered (decision: keep the main bundle slim),
//     3. registers the bot-turn trigger with the duel's animation queue:
//        whenever a Bot-format update lands with the human out of ticks, call
//        loadedBot.TakeAction(duel) after a beat (old client :6795/:4500).
// =============================================================================

import { GetSources, GetTargets, Get, shuffle, c } from '../rules.js';
import { sendAction } from './socket.js';
import { store } from '../core/store.js';
import { setBotTurnHandler, stateSig } from '../screens/duel/animations.js';

/** The self-hosted bot scripts (BotTools must load first). */
const BOT_SCRIPTS = [
	'/bots/BotTools.js',
	'/bots/BoringHuman_auri.js',
	'/bots/HornyBunny_auri.js',
	'/bots/FastGoblin_auri.js',
	'/bots/SmartFox_auri.js',
	'/bots/SmugTiger_auri.js',
	'/bots/PackLeaderWolf_heromc.js',
	'/bots/RancherCentaur_heromc.js',
	'/bots/EggGuardianDragon_heromc.js',
	'/bots/HatchlingKobold_heromc.js',
	'/bots/SwiftwingBird_heromc.js',
	'/bots/RootswornElf_heromc.js',
	'/bots/ThroneOfLies_heromc.js',
];

/** Resolves when every bot script has loaded (null until first requested). */
let loadPromise = /** @type {Promise<void>|null} */ (null);

/**
 * The bot roster shown by the challenge ladder. hero = the BOT's hero id
 * (drives ladder progression + the face art). Old DuelBotPopup :983 + the
 * SmugTiger addition already self-hosted on dev.
 */
export const BOT_ROSTER = [
	{ id: 'BoringHuman_auri', name: 'Human Bot', hero: 0 },
	{ id: 'HornyBunny_auri', name: 'Bunny Bot', hero: 1 },
	{ id: 'FastGoblin_auri', name: 'Goblin Bot', hero: 4 },
	{ id: 'SmartFox_auri', name: 'Fox Bot', hero: 5 },
	{ id: 'SmugTiger_auri', name: 'Tiger Bot', hero: 6 },
	// Wolf — owner-only while it is still being tuned. The script still loads
	// for everyone (public/bots/ is static), but it is not offered, and the
	// server refuses a Bot Game naming it: src/ai/bot-roster.js is the real
	// gate. Keep this list in step with ROSTER there.
	{ id: 'PackLeaderWolf_heromc', name: 'Wolf Bot', hero: 3, adminOnly: true },
	// The other six heroes — owner-only too, for the same reason.
	{ id: 'RancherCentaur_heromc', name: 'Rancher Bot', hero: 2, adminOnly: true },
	{ id: 'EggGuardianDragon_heromc', name: 'Dragon Bot', hero: 7, adminOnly: true },
	{ id: 'HatchlingKobold_heromc', name: 'Kobold Bot', hero: 8, adminOnly: true },
	{ id: 'SwiftwingBird_heromc', name: 'Bird Bot', hero: 9, adminOnly: true },
	{ id: 'RootswornElf_heromc', name: 'Elf Bot', hero: 10, adminOnly: true },
	{ id: 'ThroneOfLies_heromc', name: 'Throne Bot', hero: 11, adminOnly: true },
];

/**
 * The roster this account may be offered. `adminBots` comes from POST /login
 * (the server decides; the client never infers it from an id).
 * @returns {Array<{id:string, name:string, hero:number, adminOnly?:boolean}>}
 */
function visibleRoster() {
	const admin = Boolean(store.userData?.adminBots);
	return BOT_ROSTER.filter((bot) => admin || !bot.adminOnly);
}

/** How many roster scripts have registered, ignoring who may SEE them. */
function registeredCount() {
	const registry = /** @type {any} */ (window).biterbots || {};
	return BOT_ROSTER.filter((bot) => registry[bot.id]).length;
}

/**
 * Install the globals the classic bot scripts reach for.
 * Idempotent; called before the scripts load.
 */
function installBotGlobals() {
	const w = /** @type {any} */ (window);
	w.biterbots = w.biterbots || {};
	// The shared rules ARE our bundled modules — one source of truth. The
	// scripts lean on the whole legality/lookup surface: GetSources/GetTargets
	// (planning), Get (board/hand lookups inside SendAction), shuffle + c
	// (their randomness). Missing ANY of these throws mid-TakeAction — which
	// the crash-guard converts into an eternal "Crop" (the bug live testing
	// caught: the AI only ever harvested).
	w.GetSources = GetSources;
	w.GetTargets = GetTargets;
	w.Get = Get;
	w.shuffle = shuffle;
	w.c = c;
	// Bots emit their chosen action through the human player's socket — and
	// arm the watchdog, because a move the server REJECTS produces no
	// update, and no update means nothing would ever ask the bot again.
	w.SocketAction = (action) => {
		sendAction(action);
		armBotWatchdog(store.duel);
	};
	// A few scripts read the card table for name lookups.
	w.cards = store.cards;
	// Legacy globals the scripts' tail code touches: URLCODE (defined exactly
	// like the old client :33) and an optional RenderMain() nudge — a no-op
	// here, since our lobby repaints from the store on its own.
	w.URLCODE = w.URLCODE ?? (window.location.search.substring(1) || '');
}

/**
 * Load the bot scripts once (classic <script> tags, in order — BotTools
 * defines helpers the bots call).
 *
 * IMPORTANT QUIRK: the bot scripts do NOT register on execution — each wraps
 * its `biterbots.X = {...}` in a `setTimeout(..., ~400ms)` (a legacy
 * load-order workaround). So after the <script> tags finish, we must ALSO
 * wait for the registry to actually populate, or the roster reads as empty
 * on first visit.
 * @returns {Promise<void>}
 */
export function loadBots() {
	if (loadPromise) return loadPromise;
	installBotGlobals();
	loadPromise = (async () => {
		for (const src of BOT_SCRIPTS) {
			await new Promise((resolve) => {
				const script = document.createElement('script');
				script.src = src;
				script.onload = () => resolve(undefined);
				script.onerror = () => {
					console.error('bot script failed to load:', src);
					resolve(undefined); // missing bot != broken game
				};
				document.head.appendChild(script);
			});
		}
		// Wait (up to 5s) for the deferred registrations to land. Resolves as
		// soon as the full roster is in; gives up gracefully otherwise so a
		// genuinely-broken script can't hang the screen forever.
		// Count EVERY registration, not just the ones this account may see:
		// availableBots() hides admin-only bots, so waiting on its length would
		// stall an ordinary player for the whole 5 seconds, every visit.
		const deadline = Date.now() + 5000;
		while (Date.now() < deadline && registeredCount() < BOT_ROSTER.length) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}
	})();
	return loadPromise;
}

/**
 * Which bots are actually loaded + registered?
 * @returns {Array<{id:string, name:string, hero:number}>}
 */
export function availableBots() {
	const registry = /** @type {any} */ (window).biterbots || {};
	return visibleRoster().filter((bot) => registry[bot.id]);
}

/** Pending "the bot is thinking" timer — one action lined up at a time. */
let botThinkTimer = /** @type {ReturnType<typeof setTimeout>|null} */ (null);

// -----------------------------------------------------------------------------
// THE WATCHDOG. The server only broadcasts an update when it ACCEPTS an action
// (realtime/sockets.js), and this runner only asks the bot to move when an
// update commits (kickBotIfNeeded). So one rejected bot move — a script bug, a
// script cached from before a rules change, a race with a reconnect — used to
// leave the duel waiting on the bot forever, with a page refresh or Leave (a
// loss) as the player's only ways out. Now every bot send arms a timer: if the
// duel is still the same moment of the game when it fires, the bot is asked
// again (its scripts are randomized, so a second try usually differs), and
// after two silent tries the turn is passed with a harvest, which is always
// legal while the bot holds a tick. A real update clears everything.
// -----------------------------------------------------------------------------

/** How long a bot move may go unanswered before it is retried. */
const BOT_WATCHDOG_MS = 6000;

/** How many unanswered tries before the runner passes the tick with Crop. */
const BOT_WATCHDOG_RETRIES = 2;

let botWatchdog = /** @type {ReturnType<typeof setTimeout>|null} */ (null);
let botSilentSends = 0;

/** @param {any} duel  The duel the bot's move was computed on. */
function armBotWatchdog(duel) {
	clearBotWatchdog();
	if (!duel || duel.format !== 'Bot') return;
	const sentId = duel.id;
	const sentSig = stateSig(duel);
	botWatchdog = setTimeout(() => {
		botWatchdog = null;
		const now = store.duel;
		// The world moved on (an update landed, the game ended, the player
		// left, the human is acting): nothing to recover.
		if (!now || now.id !== sentId || now.GameOver || now.spectating || now.player1.tick !== 0) {
			botSilentSends = 0;
			return;
		}
		if (stateSig(now) !== sentSig) {
			botSilentSends = 0;
			return;
		}
		// Offline: the reconnect replay re-kicks the bot; a retry now would
		// only queue a stale move.
		if (store.connection !== 'connected') return;
		botSilentSends++;
		console.warn(`bot move went unanswered (${botSilentSends}); ${botSilentSends >= BOT_WATCHDOG_RETRIES ? 'passing the tick' : 'asking again'}`);
		if (botSilentSends >= BOT_WATCHDOG_RETRIES) {
			botSilentSends = 0;
			sendAction('Crop');
			armBotWatchdog(now);
			return;
		}
		onBotTurn(now);
	}, BOT_WATCHDOG_MS);
}

function clearBotWatchdog() {
	if (botWatchdog) {
		clearTimeout(botWatchdog);
		botWatchdog = null;
	}
}

/**
 * The duel screen's animation queue calls this hook whenever it's the bot's
 * move (Bot format, human out of ticks, queue drained).
 *
 * PACING: the bot doesn't act instantly — each action waits a randomized
 * "thinking" beat (~1–2s here, on top of the scripts' own ~0.7–0.95s
 * SendAction delay), so its turns read as deliberate moves instead of a
 * machine-gun burst. EXCEPT after mid-turn bookkeeping: when the bot's OWN
 * previous action was a harvest (Crop) or a Draw there's nothing to ponder,
 * so the next move comes quickly (~0.2–0.5s beat) and resource chains don't
 * drag. The TURN HANDOFF always gets the full think — a fresh bot turn
 * arrives with its clock refilled (engine: tick = 4), which is how we tell
 * the handoff apart even when our closing action was itself a harvest. The
 * timer re-checks the live state before firing: a concede, game over, or
 * turn change while thinking cancels the move.
 * @param {any} duel
 */
function onBotTurn(duel) {
	if (!duel || duel.format !== 'Bot' || duel.GameOver || duel.spectating) return;
	if (botThinkTimer) return; // an action is already lined up
	// A kick means an update committed: the previous move was answered.
	clearBotWatchdog();
	const lastAction = String(duel.history?.action || '');
	const midTurn = Number(duel.player2?.tick) < 4;
	const quick = midTurn && (lastAction === 'Crop' || lastAction === 'Draw');
	const thinkMs = quick ? 200 + Math.random() * 300 : 1000 + Math.random() * 1000;
	// ?debug: expose the chosen pacing lane for automated verification.
	if (/** @type {any} */ (window).DB) {
		/** @type {any} */ (window).DB.lastBotThink = { thinkMs, quick, lastAction };
	}
	botThinkTimer = setTimeout(() => {
		botThinkTimer = null;
		const current = store.duel;
		if (!current || current.id !== duel.id || current.GameOver || current.player1.tick !== 0) {
			// The world moved on while thinking. If a DIFFERENT bot duel is
			// already waiting on the bot (its kick was swallowed by this
			// pending timer), line that one up instead of dropping the turn.
			if (current && current.id !== duel.id && current.format === 'Bot'
				&& !current.GameOver && !current.spectating && current.player1.tick === 0) {
				onBotTurn(current);
			}
			return;
		}
		const registry = /** @type {any} */ (window).biterbots || {};
		const bot = registry[current.botName];
		if (!bot) {
			// Scripts may still be loading (e.g. reconnect straight into a bot
			// game) — ask again once they're in, through the same guarded path
			// as a normal turn (the old shortcut called TakeAction bare, with no
			// crash guard and no check that it was still the bot's move).
			void loadBots().then(() => {
				const late = (/** @type {any} */ (window).biterbots || {})[current.botName];
				if (late && store.duel?.id === current.id) {
					onBotTurn(store.duel);
				} else if (!late && store.duel?.id === current.id && store.duel.player1.tick === 0) {
					// No such brain on this client (a bot the server knows and we
					// don't): pass the tick rather than wait forever.
					console.error('no bot script registered for', current.botName, '— passing the tick');
					sendAction('Crop');
					armBotWatchdog(store.duel);
				}
			});
			return;
		}
		// Keep the card table fresh for the scripts (settings may replace it).
		/** @type {any} */ (window).cards = store.cards;
		try {
			bot.TakeAction(current);
		} catch (error) {
			// A crashed bot must not hang the game: pass the turn shape by
			// harvesting (always legal) so the server advances the duel.
			console.error('Bot TakeAction crashed:', error);
			sendAction('Crop');
			armBotWatchdog(current);
		}
	}, thinkMs);
}

// Register the trigger as soon as this module is in the bundle.
setBotTurnHandler(onBotTurn);
